export enum ComplianceType {
  hipaa = 'hipaa',
  stateRegulation = 'state_regulation',
  federalRegulation = 'federal_regulation',
  license = 'license',
  certification = 'certification',
  policy = 'policy',
  other = 'other',
}

export enum ComplianceStatus {
  compliant = 'compliant',
  nonCompliant = 'non_compliant',
  pending = 'pending',
  expired = 'expired',
  underReview = 'under_review',
}

export enum AuditType {
  internal = 'internal',
  external = 'external',
  security = 'security',
  compliance = 'compliance',
  financial = 'financial',
}

export enum AuditStatus {
  scheduled = 'scheduled',
  inProgress = 'in_progress',
  completed = 'completed',
  cancelled = 'cancelled',
}

export enum IncidentType {
  breach = 'breach',
  security = 'security',
  compliance = 'compliance',
  dataLoss = 'data_loss',
  unauthorizedAccess = 'unauthorized_access',
  other = 'other',
}

export enum IncidentSeverity {
  low = 'low',
  medium = 'medium',
  high = 'high',
  critical = 'critical',
}

export enum IncidentStatus {
  reported = 'reported',
  underInvestigation = 'under_investigation',
  resolved = 'resolved',
  closed = 'closed',
}

export interface ComplianceRecord {
  id: string;
  type: ComplianceType;
  name: string;
  description?: string;
  status: ComplianceStatus;
  requirement?: string;
  applicableTo?: string[]; // Departments, roles, etc.
  dueDate?: Date;
  completedDate?: Date;
  renewalDate?: Date;
  responsibleParty?: string;
  documents?: string[]; // Document IDs
  notes?: string;
  createdAt: Date;
  lastUpdated: Date;
}

export interface Policy {
  id: string;
  title: string;
  category: 'security' | 'privacy' | 'hr' | 'compliance' | 'operational' | 'other';
  content: string;
  version: number;
  effectiveDate: Date;
  expirationDate?: Date;
  status: 'draft' | 'active' | 'archived';
  requiresAcknowledgment: boolean;
  acknowledgmentDeadline?: Date;
  createdBy: string;
  createdByName: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
  acknowledgments?: PolicyAcknowledgment[];
  createdAt: Date;
  lastUpdated: Date;
}

export interface PolicyAcknowledgment {
  userId: string;
  userName: string;
  acknowledgedAt: Date;
  ipAddress?: string;
}

export interface Audit {
  id: string;
  type: AuditType;
  name: string;
  description?: string;
  status: AuditStatus;
  scheduledDate: Date;
  startDate?: Date;
  endDate?: Date;
  conductedBy?: string;
  conductedByName?: string;
  scope?: string;
  findings?: AuditFinding[];
  recommendations?: string[];
  reportUrl?: string;
  createdAt: Date;
  lastUpdated: Date;
}

export interface AuditFinding {
  id: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  recommendation?: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  assignedTo?: string;
  dueDate?: Date;
  resolvedDate?: Date;
  notes?: string;
}

export interface ComplianceIncident {
  id: string;
  type: IncidentType;
  severity: IncidentSeverity;
  status: IncidentStatus;
  title: string;
  description: string;
  reportedBy: string;
  reportedByName: string;
  reportedAt: Date;
  affectedEntities?: string[]; // User IDs, employer IDs, etc.
  affectedData?: string; // Description of affected data
  discoveredAt: Date;
  containedAt?: Date;
  resolvedAt?: Date;
  investigationNotes?: string;
  remediationSteps?: string[];
  notificationRequired: boolean;
  notifiedAt?: Date;
  notificationRecipients?: string[];
  followUpRequired: boolean;
  followUpDate?: Date;
  createdAt: Date;
  lastUpdated: Date;
}

export interface HIPAAComplianceLog {
  id: string;
  userId: string;
  userName: string;
  action: string; // e.g., "accessed PHI", "viewed member record"
  entityType: string; // e.g., "member", "receipt", "employer"
  entityId: string;
  ipAddress?: string;
  timestamp: Date;
  reason?: string;
  metadata?: Record<string, unknown>;
}

export interface BusinessAssociateAgreement {
  id: string;
  vendorName: string;
  vendorContact?: string;
  agreementDate: Date;
  expirationDate?: Date;
  status: 'active' | 'expired' | 'pending_renewal';
  documentUrl: string;
  signedBy: string;
  signedByName: string;
  notes?: string;
  createdAt: Date;
  lastUpdated: Date;
}

